import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { ROLE_LABELS, CHANNEL_LABELS } from './constants';
import './Screens.css';

// Sends one message per recipient and logs each send against that contact's account,
// so the activity timeline stays the single record of what was said and when.
//
// Recipients come from two places: the contact list, and an address typed straight in.
// Reps email people who aren't in the CRM constantly -- a coach who just took over, an
// AD copied on a quote -- so the same box that filters contacts also accepts an address
// on Enter. A typed address that already belongs to a contact still files against them.
//
// Who it comes from is decided server-side and is always the rep's own address; there is
// no shared-mailbox fallback, because mail from "HUB Notifications" is not mail from the
// person the coach deals with. `sentFrom` is what the server reports back, so this never
// claims something different from what actually went out -- and when it is empty, the
// form offers the mailbox connection instead of a send that would be refused.

const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Compose = ({ onSent, sentFrom, onConnectMailbox }) => {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [typed, setTyped] = useState([]);
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchContacts = useCallback(async () => {
    try {
      const response = await api.get('/crm/contacts?limit=500');
      setContacts(response.data.contacts.filter(c => c.email));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load contacts');
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const filtered = useMemo(() => {
    if (!search) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(c =>
      `${c.first_name} ${c.last_name || ''} ${c.email} ${c.organization_name}`.toLowerCase().includes(q)
    );
  }, [contacts, search]);

  const entry = search.trim();
  const canAddTyped = LOOKS_LIKE_EMAIL.test(entry);

  const toggle = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  // Adding an address that belongs to a contact checks that contact instead, so the same
  // person can't end up in the list twice under two different labels.
  const addTyped = () => {
    if (!canAddTyped) return;
    const address = entry;
    const key = address.toLowerCase();
    const match = contacts.find(c => (c.email || '').toLowerCase() === key);
    if (match) {
      setSelected(prev => (prev.includes(match.id) ? prev : [...prev, match.id]));
    } else {
      setTyped(prev => (prev.some(a => a.toLowerCase() === key) ? prev : [...prev, address]));
    }
    setSearch('');
    setError('');
  };

  const removeTyped = (address) =>
    setTyped(prev => prev.filter(a => a !== address));

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Enter in a lone-input form would submit a half-written email otherwise.
      e.preventDefault();
      addTyped();
    }
  };

  const total = selected.length + typed.length;

  const send = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    // An address left sitting in the box is what the rep meant to send to.
    let pendingTyped = typed;
    if (canAddTyped) {
      const key = entry.toLowerCase();
      const match = contacts.find(c => (c.email || '').toLowerCase() === key);
      if (!match && !typed.some(a => a.toLowerCase() === key)) {
        pendingTyped = [...typed, entry];
        setTyped(pendingTyped);
      } else if (match && !selected.includes(match.id)) {
        setSelected(prev => [...prev, match.id]);
      }
      setSearch('');
    }

    const contactIds = canAddTyped
      ? Array.from(new Set([
        ...selected,
        ...contacts.filter(c => (c.email || '').toLowerCase() === entry.toLowerCase()).map(c => c.id),
      ]))
      : selected;

    if (contactIds.length + pendingTyped.length === 0) {
      return setError(entry
        ? `"${entry}" is not a valid email address — pick a contact or type a full address`
        : 'Pick a contact, or type an email address and press Enter');
    }
    if (!subject.trim()) return setError('Subject is required');
    if (!body.trim()) return setError('Write a message');

    try {
      setSending(true);
      const response = await api.post('/crm/email', {
        contact_ids: contactIds,
        emails: pendingTyped,
        subject: subject.trim(),
        body: body.trim(),
      });
      setResult(response.data);
      if (response.data.sent.length > 0) {
        if (onSent) onSent();
        setSelected([]);
        setTyped([]);
        setSubject('');
        setBody('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {error && <p className="banner error">{error}</p>}
      {result && (
        <p className={`banner ${result.sent.length ? 'ok' : 'error'}`}>
          Sent to {result.sent.length} {result.sent.length === 1 ? 'person' : 'people'}
          {result.failed.length > 0 && ` · ${result.failed.length} failed (${result.failed.join(', ')})`}
          {result.skipped > 0 && ` · ${result.skipped} skipped with no email on file`}
        </p>
      )}

      <form className="email-grid" onSubmit={send}>
        <div className="card">
          <h2>Recipients {total > 0 && <span className="pill pay-invoiced">{total}</span>}</h2>
          <div className="field">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Filter contacts, or type an email address…"
            />
            {canAddTyped ? (
              <button type="button" className="btn add-typed" onClick={addTyped}>
                Add {entry}
              </button>
            ) : (
              <span className="muted hint">
                Anyone not in the CRM: type their full address and press Enter.
              </span>
            )}
          </div>

          {typed.length > 0 && (
            <ul className="typed-recipients">
              {typed.map(address => (
                <li key={address}>
                  <span>{address}</span>
                  <button
                    type="button"
                    onClick={() => removeTyped(address)}
                    aria-label={`Remove ${address}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {contacts.length === 0 ? (
            <p className="empty" style={{ padding: '12px 0' }}>
              No contacts have an email address on file yet.
            </p>
          ) : (
            <div className="recipient-picker">
              <ul>
                {filtered.map(c => (
                  <li key={c.id}>
                    <input
                      type="checkbox"
                      id={`c-${c.id}`}
                      checked={selected.includes(c.id)}
                      onChange={() => toggle(c.id)}
                    />
                    <label htmlFor={`c-${c.id}`} style={{ cursor: 'pointer' }}>
                      <div className="r-name">{[c.first_name, c.last_name].filter(Boolean).join(' ')}</div>
                      <div className="r-sub">
                        {c.organization_name}
                        {c.role ? ` · ${ROLE_LABELS[c.role]}` : ''}
                      </div>
                      <div className="r-sub">{c.email}</div>
                      {c.preferred_channel === 'text' && (
                        <div className="r-sub" style={{ color: '#a37500' }}>
                          Prefers {CHANNEL_LABELS.text.toLowerCase()}
                        </div>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="card">
          <div className="field">
            <span className="field-label">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Fall football order — sizing sheet"
            />
          </div>

          <div className="field">
            <span className="field-label">Message</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={'Coach,\n\nWanted to get your order started before the window closes...'}
            />
          </div>

          <div className="screen-actions" style={{ justifyContent: 'space-between' }}>
            <span className="muted" style={{ fontSize: '0.8125rem' }}>
              {sentFrom
                ? `Sending as ${sentFrom} — replies come back to you.`
                : 'Connect your mailbox to send. CRM email goes out as you, never as HUB.'}
            </span>
            {sentFrom ? (
              <button type="submit" className="btn primary" disabled={sending}>
                {sending ? 'Sending…' : `Send${total ? ` to ${total}` : ''}`}
              </button>
            ) : (
              <button type="button" className="btn primary" onClick={onConnectMailbox}>
                Connect your mailbox
              </button>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default Compose;
